import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { InformationCircle } from 'assets/icons';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

const ModalHolidayHoursCompany = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const [percentage, setPercentage] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [solidarityDay, setSolidarityDay] = useState('lundi_de_pentecote');
  const [holidaysError, setHolidaysError] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    if (!modalData?.idCompany) return;
    ApiDatabase.getHolidayHoursCompany(
      { token, idCompany: modalData.idCompany },
      (data) => {
        // The modal opens with the current percentage and solidarity day
        // pre-filled: it serves for the initial entry as well as for edits (RG-1).
        const current = data?.holidayHours?.percentage;
        setPercentage(current === undefined || current === null ? '' : String(current));
        if (data?.holidayHours?.solidarityDay) {
          setSolidarityDay(data.holidayHours.solidarityDay);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );

    ApiDatabase.getHolidays(
      { token, idCompany: modalData.idCompany },
      (data) => {
        setHolidays(data.holidays ?? []);
        setHolidaysError(null);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        // An empty dropdown with no explanation reads as "there is nothing to
        // choose", which is not what happened.
        setHolidaysError(
          "La liste des jours fériés n'a pas pu être chargée. Réessayez plus tard."
        );
      }
    );
  }, [modalData?.idCompany]);

  const validate = (value) => {
    const parsed = parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return 'Saisissez un nombre.';
    if (parsed < 0 || parsed > 100)
      return 'La majoration doit être comprise entre 0 et 100.';
    if (Math.round(parsed * 10) / 10 !== parsed)
      return 'La majoration accepte au plus une décimale.';
    return null;
  };

  const handleSubmit = () => {
    const invalid = validate(percentage);
    if (invalid) return setError(invalid);

    setIsLoading(true);
    ApiDatabase.postHolidayHoursCompany(
      {
        token,
        idCompany: modalData.idCompany,
        percentage: parseFloat(String(percentage).replace(',', '.')),
        solidarityDay,
      },
      (data) => {
        setIsLoading(false);
        toast.success('La majoration des heures de jours fériés a été enregistrée');
        setModalDataReturn({ type: 'holiday_hours_updated', holidayHours: data.holidayHours });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        if (err.name === 'CanceledError') return;
        // The modal stays open, the input intact (RG-6).
        setError(err?.response?.data?.message ?? "L'enregistrement a échoué");
      }
    );
  };

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Heures jours fériés travaillés
      </Modalheader>
      <ModalBody>
        <div className={'flex flex-col gap-3 text-sm'}>
          <div className={'text-gray-500'}>
            Les majorations que vous définissez ici s’appliquent à tous les
            jours fériés travaillés (hors journée de solidarité).
          </div>
          <label className={'flex flex-col gap-1'}>
            Majoration
            <div className={'flex items-center gap-2'}>
              <input
                type={'text'}
                className={`border rounded px-2 py-2 w-full ${error ? 'border-red-500' : ''}`}
                value={percentage}
                onChange={(e) => {
                  setPercentage(e.target.value);
                  setError(null);
                }}
              />
              <span className={'text-gray-500'}>%</span>
            </div>
          </label>
          {error && <div className={'text-red-500 text-xs'}>{error}</div>}
          <label className={'flex flex-col gap-1'}>
            <span className={'flex items-center justify-between'}>
              Journée de solidarité
              <span
                title={
                  "La journée de solidarité est travaillée sans majoration : elle finance l’autonomie des personnes âgées et handicapées. Le jour choisi ici est exclu du calcul."
                }
                className={'cursor-help'}
              >
                <InformationCircle wh={20} color={'#9CA3AF'} />
              </span>
            </span>
            <select
              className={'border rounded px-2 py-2'}
              value={solidarityDay}
              disabled={holidays.length === 0}
              onChange={(e) => setSolidarityDay(e.target.value)}
            >
              {holidays.length === 0 && (
                <option value={solidarityDay}>
                  {holidaysError ? 'Indisponible' : 'Chargement…'}
                </option>
              )}
              {holidays.map((holiday) => (
                <option key={holiday.slug} value={holiday.slug}>
                  {holiday.label}
                </option>
              ))}
            </select>
            {holidaysError && (
              <div className={'text-red-500 text-xs'}>{holidaysError}</div>
            )}
          </label>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button size={'LG'} color={'SECONDARY'} onClick={toggle}>
          Annuler
        </Button>
        <Button
          size={'LG'}
          color={isLoading ? 'DISABLED' : 'PRIMARY'}
          onClick={isLoading ? undefined : handleSubmit}
        >
          {isLoading ? 'Confirmation ...' : 'Confirmer'}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ModalHolidayHoursCompany;
